import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "concrete-web-design-castle-rock",
  metaTitle: 'Concrete & Flatwork Web Design in Castle Rock, CO | Websites That Book Pours',
  metaDescription: 'Websites for Castle Rock concrete and flatwork contractors — patio, driveway, and stamped concrete pages built to win Douglas County quote requests. Call (719) 419-3935.',
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Castle Rock web design company", to: "/web-design-castle-rock-colorado" },
  ],
  currentLabel: "Concrete & Flatwork Web Design Castle Rock",
  eyebrow: "Concrete & Flatwork Web Design — Castle Rock, CO",
  h1: (
    <>
      Concrete & Flatwork <span className="text-brand-gold">Web Design</span> in Castle Rock
    </>
  ),
  heroDescription: "Fast, mobile-friendly websites for Castle Rock concrete and flatwork contractors, built to turn local searches into quote requests.",
  intro: 'Castle Rock homeowners compare two or three concrete contractors before they call. We build concrete websites with a page for every job type — patios, driveways, stamped and decorative work, foundations — plus real project photos and a simple quote form, so the right buyers in The Meadows, Crystal Valley, and Castle Pines pick you.',
  definition: {
    term: "Concrete & Flatwork web design in Castle Rock",
    body: "A concrete and flatwork website built for Castle Rock buyers: one page per service, real project photos, clear calls to action, and local town pages so Douglas County homeowners can find and trust you.",
  },
  whatWeDo: [
    { title: "A page for every service", body: 'Patios, driveways, stamped concrete, foundations, retaining walls, and repair — each gets its own page so it can show up for its own search.' },
    { title: "Real project galleries", body: "Your own finished work, shown clearly on phones and desktops." },
    { title: "Simple quote forms", body: 'Quote forms that ask for square footage, job type, and timing so you only spend time on real projects.' },
    { title: "Douglas County town pages", body: "Castle Rock, Castle Pines, Parker, Larkspur, and Franktown pages for the areas you serve." },
    { title: "Google Maps ready", body: "Your site and Google Business Profile tell the same story, so you show up in local results." },
    { title: "SEO-ready from launch", body: "Clean structure, local business details, and fast pages built in from day one." },
  ],
  whyThisMarket: [
    { heading: 'Spring demand decides the year', body: 'Most Douglas County patio and driveway calls come in spring and early summer. A site that is ready before the snow melts catches that rush.' },
    { heading: 'Photos sell the job', body: 'Concrete is a visual sale. Galleries of your own finished pours build trust faster than any sales copy.' },
    { heading: 'Town pages bring nearby calls', body: 'Pages for Castle Rock, Castle Pines, Parker, and Larkspur help you show up for the towns you actually serve.' },
  ],
  relatedLinks: [
    { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
    { to: "/castle-rock", label: "Castle Rock digital marketing" },
    { to: "/south-metro-contractor-marketing", label: "South Metro contractor marketing hub" },
    { to: "/learn/concrete-leads-colorado", label: "Concrete & Flatwork lead guide for Colorado" },
    { to: "/concrete-web-design-colorado", label: "Concrete & Flatwork web design across Colorado" },
    { to: "/learn/organic-leads-colorado-contractors", label: "Free leads first: a guide for Colorado contractors" },
    { to: "/pricing", label: "Website pricing" },
  ],
  faqs: [
    { q: "How much does a concrete and flatwork website cost in Castle Rock?", a: "Website packages start at $1,850 one-time and include one year of hosting. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take?", a: "Typical sites are ready in under seven business days once we have your photos and service details." },
    { q: "Do you serve areas outside Castle Rock?", a: "Yes — Castle Pines, Parker, Larkspur, Franktown, Highlands Ranch, and the rest of the south metro and Front Range." },
  ],
  serviceName: "Concrete & Flatwork Web Design in Castle Rock, CO",
  serviceDescription: "Mobile-friendly websites for concrete and flatwork contractors in Castle Rock and Douglas County.",
  areaServed: "Castle Rock, CO",
};

export const Route = createFileRoute("/concrete-web-design-castle-rock")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
